/**
 * Équipe et pointage des heures.
 *
 * Deux exigences dans un seul module, et c'est délibéré : le décompte quotidien
 * imposé par L.3171-2 et le coût de main-d'œuvre des chantiers portent sur la
 * même donnée. Les tenir séparément aurait produit deux saisies pour un seul
 * geste — la manière la plus sûre de n'en avoir aucune des deux.
 *
 * La répartition des rôles suit qui fait quoi sur un chantier :
 *
 *   · le **conducteur** pointe son équipe — c'est lui qui sait qui était où ;
 *   · le **patron** seul touche aux rémunérations, et la base le lui impose
 *     par une policy plutôt que ce fichier par un `allowedRoles`. Voir
 *     `employee_costs` : la RLS filtre des lignes et non des colonnes, d'où
 *     une table à part.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ExecutionContext, Role } from "../../context.js";
import { computeWeeklyTally, startOfWeek } from "./types.js";
import type {
  Employee,
  Invitation,
  TimeEntry,
  TimeEntryKind,
  WeeklyTally,
} from "./types.js";

// ─── Entrées ───────────────────────────────────────────────────────────────

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ");

export const listEmployeesInput = z.object({
  confirmationId: z.string().optional(),
  /** Les salariés partis restent lisibles : leurs heures sont dans les coûts. */
  includeInactive: z.boolean().default(false),
});

export const saveEmployeeInput = z.object({
  confirmationId: z.string().optional(),
  employeeId: z.string().uuid().optional(),
  firstName: z.string().trim().min(1, "Le prénom est obligatoire"),
  lastName: z.string().trim().min(1, "Le nom est obligatoire"),
  jobTitle: z.string().trim().max(80).nullish(),
  phone: z.string().trim().max(20).nullish(),
  weeklyHours: z.coerce
    .number()
    .positive("La durée hebdomadaire doit être supérieure à zéro")
    .max(60, "Au-delà de 60 h, ce n'est plus un horaire contractuel"),
  hiredOn: isoDate.nullish(),
});

export const archiveEmployeeInput = z.object({
  confirmationId: z.string().optional(),
  employeeId: z.string().uuid(),
});

export const setHourlyCostInput = z.object({
  confirmationId: z.string().optional(),
  employeeId: z.string().uuid(),
  /** Coût **chargé** : salaire brut plus charges. Seul chiffre qui ait un sens
   *  dans une marge de chantier. */
  hourlyCost: z.coerce.number().min(0, "Un coût horaire ne peut pas être négatif"),
});

/**
 * Émettre un code d'accès.
 *
 * `employeeId` est facultatif : une invitation naît presque toujours de la
 * fiche d'un salarié — c'est ce lien qui remplit `employees.user_id` — mais un
 * comptable en `lecture_seule` n'est sur aucune feuille de paie, et exiger un
 * salarié obligerait à en inventer un.
 */
export const inviteUserInput = z.object({
  confirmationId: z.string().optional(),
  employeeId: z.string().uuid().nullish(),
  role: z
    .enum(["patron", "conducteur", "assistant", "chef_de_chantier", "ouvrier", "lecture_seule"])
    .default("ouvrier"),
});

export const revokeInvitationInput = z.object({
  confirmationId: z.string().optional(),
  invitationId: z.string().uuid(),
});

export const revokeAccessInput = z.object({
  confirmationId: z.string().optional(),
  employeeId: z.string().uuid(),
});

export const listInvitationsInput = z.object({
  confirmationId: z.string().optional(),
});

export const readWeekInput = z.object({
  confirmationId: z.string().optional(),
  /** N'importe quel jour de la semaine voulue. */
  weekOf: isoDate,
  employeeId: z.string().uuid().optional(),
});

export const recordTimeInput = z.object({
  confirmationId: z.string().optional(),
  employeeId: z.string().uuid(),
  projectId: z.string().uuid().nullish(),
  workDate: isoDate,
  hours: z.coerce
    .number()
    .positive("Un pointage porte sur des heures travaillées")
    .max(24, "Une journée ne dépasse pas 24 heures"),
  kind: z
    .enum(["work", "travel", "weather", "paid_leave", "unpaid_leave", "sick", "training"])
    .default("work"),
  notes: z.string().trim().max(500).nullish(),
});

export const deleteTimeInput = z.object({
  confirmationId: z.string().optional(),
  entryId: z.string().uuid(),
});

export type ListEmployeesInput = z.infer<typeof listEmployeesInput>;
export type SaveEmployeeInput = z.infer<typeof saveEmployeeInput>;
export type ArchiveEmployeeInput = z.infer<typeof archiveEmployeeInput>;
export type SetHourlyCostInput = z.infer<typeof setHourlyCostInput>;
export type InviteUserInput = z.infer<typeof inviteUserInput>;
export type RevokeInvitationInput = z.infer<typeof revokeInvitationInput>;
export type RevokeAccessInput = z.infer<typeof revokeAccessInput>;
export type ListInvitationsInput = z.infer<typeof listInvitationsInput>;
export type ReadWeekInput = z.infer<typeof readWeekInput>;
export type RecordTimeInput = z.infer<typeof recordTimeInput>;
export type DeleteTimeInput = z.infer<typeof deleteTimeInput>;

// ─── Sorties ───────────────────────────────────────────────────────────────

/** Une semaine de pointage, telle que la grille l'affiche. */
export interface WeekTimesheet {
  weekStart: string;
  employees: Employee[];
  entries: TimeEntry[];
  /** Un décompte par salarié, dans l'ordre de `employees`. */
  tallies: WeeklyTally[];
}

export interface SaveEmployeeCommand {
  employeeId?: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  phone: string | null;
  weeklyHours: number;
  hiredOn: string | null;
}

export interface RecordTimeCommand {
  employeeId: string;
  projectId: string | null;
  workDate: string;
  hours: number;
  kind: TimeEntryKind;
  notes: string | null;
}

export interface TeamRepository {
  listEmployees(ctx: ExecutionContext, includeInactive: boolean): Promise<Employee[]>;
  saveEmployee(ctx: ExecutionContext, command: SaveEmployeeCommand): Promise<Employee>;
  archiveEmployee(ctx: ExecutionContext, employeeId: string): Promise<void>;
  setHourlyCost(ctx: ExecutionContext, employeeId: string, cost: number): Promise<void>;

  listInvitations(ctx: ExecutionContext): Promise<Invitation[]>;
  /** Rend le code **en clair** : la base ne le rendra jamais une seconde fois. */
  createInvitation(
    ctx: ExecutionContext,
    command: { employeeId: string | null; role: Role },
  ): Promise<string>;
  revokeInvitation(ctx: ExecutionContext, invitationId: string): Promise<void>;
  revokeAccess(ctx: ExecutionContext, employeeId: string): Promise<void>;

  listEntries(
    ctx: ExecutionContext,
    range: { from: string; to: string; employeeId?: string },
  ): Promise<TimeEntry[]>;
  recordTime(ctx: ExecutionContext, command: RecordTimeCommand): Promise<TimeEntry>;
  deleteTime(ctx: ExecutionContext, entryId: string): Promise<void>;
}

// ─── Services ──────────────────────────────────────────────────────────────

export function createTeamServices(repo: TeamRepository) {
  const listerEquipe = defineService<ListEmployeesInput, Employee[]>({
    name: "lister_equipe",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.listEmployees(ctx, input.includeInactive),
  });

  const enregistrerSalarie = defineService<SaveEmployeeInput, Employee>({
    name: "enregistrer_salarie",
    level: ActionLevel.ReversibleWrite,
    // Le conducteur monte son équipe ; l'ouvrier ne s'inscrit pas lui-même.
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      repo.saveEmployee(ctx, {
        ...(input.employeeId ? { employeeId: input.employeeId } : {}),
        firstName: input.firstName,
        lastName: input.lastName,
        jobTitle: input.jobTitle ?? null,
        phone: input.phone ?? null,
        weeklyHours: input.weeklyHours,
        hiredOn: input.hiredOn ?? null,
      }),
  });

  const archiverSalarie = defineService<ArchiveEmployeeInput, void>({
    name: "archiver_salarie",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur"],
    handler: (ctx, input) => repo.archiveEmployee(ctx, input.employeeId),
  });

  const definirCoutHoraire = defineService<SetHourlyCostInput, void>({
    name: "definir_cout_horaire",
    level: ActionLevel.ReversibleWrite,
    // Doublon assumé avec la policy `patron_only` : celle-ci tient face à un
    // appel direct à PostgREST, celui-là donne un refus lisible plutôt qu'une
    // ligne qui disparaît sans explication.
    allowedRoles: ["patron"],
    handler: (ctx, input) => repo.setHourlyCost(ctx, input.employeeId, input.hourlyCost),
  });

  const listerInvitations = defineService<ListInvitationsInput, Invitation[]>({
    name: "lister_invitations",
    level: ActionLevel.Read,
    // La policy `patron_only` rendrait une liste vide aux autres rôles, ce qui
    // ressemble à « aucune invitation » alors que c'est « pas votre affaire ».
    // Le refus explicite se lit.
    allowedRoles: ["patron"],
    handler: (ctx) => repo.listInvitations(ctx),
  });

  /**
   * Émettre un code d'accès.
   *
   * **N2 et non N3**, et le choix mérite d'être écrit puisqu'il se discute :
   * distribuer un accès est l'acte le plus lourd de conséquences de
   * l'application. Mais N3 désigne dans ce dépôt ce qui sort de l'entreprise
   * ou ne se reprend pas — un devis parti, une mise en demeure. Une invitation
   * n'envoie rien à personne et se révoque tant qu'elle n'a pas servi. La
   * garder en N2 avec `allowedRoles: ["patron"]` dit exactement ce qu'elle
   * est, comme `definirCoutHoraire` juste au-dessus.
   *
   * Le doublon avec le contrôle de rôle en base est assumé, et c'est le même
   * partage qu'ailleurs : la policy tient face à un appel direct à PostgREST,
   * ce champ donne un refus lisible plutôt qu'une erreur SQL.
   */
  const inviterAuCompte = defineService<InviteUserInput, string>({
    name: "inviter_au_compte",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) =>
      repo.createInvitation(ctx, {
        employeeId: input.employeeId ?? null,
        role: input.role,
      }),
  });

  const revoquerInvitation = defineService<RevokeInvitationInput, void>({
    name: "revoquer_invitation",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) => repo.revokeInvitation(ctx, input.invitationId),
  });

  const retirerAcces = defineService<RevokeAccessInput, void>({
    name: "retirer_acces",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) => repo.revokeAccess(ctx, input.employeeId),
  });

  const lireSemaine = defineService<ReadWeekInput, WeekTimesheet>({
    name: "lire_semaine_pointage",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const weekStart = startOfWeek(input.weekOf);
      const to = new Date(Date.parse(`${weekStart}T00:00:00Z`) + 6 * 86_400_000)
        .toISOString()
        .slice(0, 10);

      const [employees, entries] = await Promise.all([
        repo.listEmployees(ctx, false),
        repo.listEntries(ctx, {
          from: weekStart,
          to,
          ...(input.employeeId ? { employeeId: input.employeeId } : {}),
        }),
      ]);

      const retenus = input.employeeId
        ? employees.filter((e) => e.id === input.employeeId)
        : employees;

      return {
        weekStart,
        employees: retenus,
        entries,
        tallies: retenus.map((e) => computeWeeklyTally(e, weekStart, entries)),
      };
    },
  });

  const saisirPointage = defineService<RecordTimeInput, TimeEntry>({
    name: "saisir_pointage",
    // Réversible : une ligne mal saisie se corrige ou se supprime, et le coût
    // du chantier se recalcule tout seul.
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: async (ctx, input) => {
      // Pointer dans le futur est presque toujours une faute de frappe sur
      // l'année, et le registre légal constate ce qui a eu lieu — il
      // n'annonce pas ce qui est prévu. Le planning, lui, viendra plus tard.
      const aujourdhui = new Date().toISOString().slice(0, 10);
      if (input.workDate > aujourdhui) {
        throw new DomainError(
          "invalid_input",
          "On ne pointe pas des heures à venir : le registre constate ce qui " +
            "a eu lieu. Vérifiez la date.",
          { workDate: input.workDate },
        );
      }

      return repo.recordTime(ctx, {
        employeeId: input.employeeId,
        projectId: input.projectId ?? null,
        workDate: input.workDate,
        hours: input.hours,
        kind: input.kind,
        notes: input.notes ?? null,
      });
    },
  });

  const supprimerPointage = defineService<DeleteTimeInput, void>({
    name: "supprimer_pointage",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => repo.deleteTime(ctx, input.entryId),
  });

  return {
    listerEquipe,
    enregistrerSalarie,
    archiverSalarie,
    definirCoutHoraire,
    listerInvitations,
    inviterAuCompte,
    revoquerInvitation,
    retirerAcces,
    lireSemaine,
    saisirPointage,
    supprimerPointage,
  };
}

export type TeamServices = ReturnType<typeof createTeamServices>;
